/**
 * FriendStatsPage Component
 * Shows simplified stats for a friend's watched movies,
 * plus a browsable grid of their watched movies with sort/filter.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getFriendStats,
  getFriendWatchedMovies,
  type FriendStats,
  type GetWatchedMoviesParams,
  type WatchedMovieItem,
  type WatchedMoviesListResponse,
} from '../api';
import { formatRuntime, RankedList, StatCard, StatsSection } from './StatsComponents';

// ISO 639-1 language code to display name mapping
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  hi: 'Hindi',
  ar: 'Arabic',
  ru: 'Russian',
  nl: 'Dutch',
  sv: 'Swedish',
  da: 'Danish',
  no: 'Norwegian',
  fi: 'Finnish',
  pl: 'Polish',
  tr: 'Turkish',
  th: 'Thai',
  id: 'Indonesian',
  ms: 'Malay',
  vi: 'Vietnamese',
  tl: 'Filipino',
  cs: 'Czech',
  el: 'Greek',
  he: 'Hebrew',
  hu: 'Hungarian',
  ro: 'Romanian',
  uk: 'Ukrainian',
  bn: 'Bengali',
  ta: 'Tamil',
  te: 'Telugu',
  ml: 'Malayalam',
  cn: 'Cantonese',
  fa: 'Persian',
  la: 'Latin',
};

function getLanguageName(code: string): string {
  return LANGUAGE_NAMES[code] || code.toUpperCase();
}

function MovieGrid({ movies, loading }: { movies: WatchedMovieItem[]; loading: boolean }) {
  if (loading && movies.length === 0) {
    return <div className="empty">Loading movies...</div>;
  }

  if (movies.length === 0) {
    return <div className="empty">No movies match your filters</div>;
  }

  return (
    <div className="stats-movie-grid">
      {movies.map((movie) => (
        <Link key={movie.tmdb_id} to={`/movies/${movie.tmdb_id}`} className="stats-movie-card">
          {movie.poster_url ? (
            <img src={movie.poster_url} alt={movie.title || ''} loading="lazy" />
          ) : (
            <div className="stats-movie-no-poster">{movie.title || '?'}</div>
          )}
          <div className="stats-movie-info">
            <div className="stats-movie-title">{movie.original_title || movie.title}</div>
            <div className="stats-movie-meta">
              {movie.year && <span>{movie.year}</span>}
              {movie.runtime && <span>{movie.runtime}m</span>}
              {movie.vote_average ? <span>{movie.vote_average.toFixed(1)}</span> : null}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function FriendStatsPage() {
  const { id } = useParams<{ id: string }>();
  const [stats, setStats] = useState<FriendStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Browse state
  const [moviesList, setMoviesList] = useState<WatchedMoviesListResponse | null>(null);
  const [moviesLoading, setMoviesLoading] = useState(false);
  const [browsePage, setBrowsePage] = useState(1);
  const [browseSort, setBrowseSort] = useState<GetWatchedMoviesParams['sort']>('watched_at');
  const [browseOrder, setBrowseOrder] = useState<'asc' | 'desc'>('desc');
  const [browseGenre, setBrowseGenre] = useState<string>('');
  const [browseLanguage, setBrowseLanguage] = useState<string>('');

  const friendId = id ? parseInt(id, 10) : NaN;

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    async function loadStats() {
      setLoading(true);
      setError(null);
      try {
        const data = await getFriendStats(parseInt(id!, 10));
        if (!cancelled) setStats(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Load browse movies when filters change
  const loadMovies = useCallback(async () => {
    if (isNaN(friendId)) return;
    setMoviesLoading(true);
    try {
      const data = await getFriendWatchedMovies(friendId, {
        page: browsePage,
        limit: 20,
        sort: browseSort,
        order: browseOrder,
        genre: browseGenre || undefined,
        language: browseLanguage || undefined,
      });
      setMoviesList(data);
    } catch (err) {
      console.error('Failed to load friend movies list:', err);
    } finally {
      setMoviesLoading(false);
    }
  }, [friendId, browsePage, browseSort, browseOrder, browseGenre, browseLanguage]);

  useEffect(() => {
    if (stats && stats.total_movies > 0) {
      loadMovies();
    }
  }, [stats, loadMovies]);

  // Reset page when filters change
  useEffect(() => {
    setBrowsePage(1);
  }, [browseSort, browseOrder, browseGenre, browseLanguage]);

  if (loading) {
    return (
      <div className="stats-loading">
        <div className="stats-loading-spinner" />
        <p>Loading friend stats...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '32px' }}>
        <p style={{ color: '#f87171' }}>{error}</p>
        <Link
          to="/friends"
          className="link-button"
          style={{ display: 'inline-flex', marginTop: '12px' }}
        >
          Back to Friends
        </Link>
      </div>
    );
  }

  if (!stats || stats.total_movies === 0) {
    return (
      <div className="stats-page">
        <div className="page-header">
          <div className="header-content">
            <Link to="/friends" className="back-link">
              &larr; Back to Friends
            </Link>
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
          <h2 style={{ marginBottom: '12px' }}>No Movies Watched Yet</h2>
          <p style={{ opacity: 0.7 }}>
            {stats?.friend.name || stats?.friend.username || 'This friend'} hasn't watched any
            movies yet.
          </p>
        </div>
      </div>
    );
  }

  const friendName = stats.friend.name || `@${stats.friend.username}`;

  return (
    <div className="stats-page">
      <div className="page-header">
        <div className="header-content">
          <Link to="/friends" className="back-link">
            &larr; Back to Friends
          </Link>
          <div className="friend-stats-header">
            {stats.friend.picture ? (
              <img src={stats.friend.picture} alt="" className="friend-stats-avatar" />
            ) : (
              <div className="friend-stats-avatar placeholder">
                {(stats.friend.username || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="stats-page-title">{friendName}'s Stats</h2>
              {stats.friend.name && stats.friend.username && (
                <p className="friend-stats-username">@{stats.friend.username}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hero stat cards */}
      <div className="stats-hero">
        <StatCard label="Watched" value={stats.total_movies} sub="movies" />
        <StatCard
          label="Watch Time"
          value={formatRuntime(stats.total_runtime_minutes)}
          sub={`${Math.round(stats.total_runtime_minutes / 60)} hrs`}
        />
        {stats.ratings.average_imdb && (
          <StatCard label="Avg. Rating" value={stats.ratings.average_imdb.toFixed(1)} sub="IMDb" />
        )}
      </div>

      {/* Oldest/newest trivia */}
      {(stats.oldest_movie || stats.newest_movie) && (
        <div className="stats-trivia">
          {stats.oldest_movie && (
            <div className="stats-trivia-item">
              <span className="stats-trivia-label">Oldest</span>
              <span>
                {stats.oldest_movie.title} ({stats.oldest_movie.year})
              </span>
            </div>
          )}
          {stats.newest_movie && (
            <div className="stats-trivia-item">
              <span className="stats-trivia-label">Newest</span>
              <span>
                {stats.newest_movie.title} ({stats.newest_movie.year})
              </span>
            </div>
          )}
        </div>
      )}

      {/* Top Directors */}
      <StatsSection title="Top Directors">
        <RankedList items={stats.top_directors} emptyMessage="No director data available" />
      </StatsSection>

      {/* Top Actors */}
      <StatsSection title="Top Actors">
        <RankedList items={stats.top_actors} emptyMessage="No actor data available" />
      </StatsSection>

      {/* Browse watched movies */}
      <StatsSection title="Watched Movies">
        <div className="stats-browse-controls">
          {/* Sort row */}
          <div className="stats-browse-sort-row">
            <select
              value={browseSort}
              onChange={(e) => setBrowseSort(e.target.value as GetWatchedMoviesParams['sort'])}
              className="stats-sort-select"
            >
              <option value="watched_at">Date Watched</option>
              <option value="title">Title</option>
              <option value="year">Year</option>
              <option value="rating">Rating</option>
            </select>

            <button
              className="secondary stats-sort-btn"
              onClick={() => setBrowseOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
              title={browseOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {browseOrder === 'asc' ? (
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 5v14M19 12l-7 7-7-7" />
                </svg>
              )}
            </button>

            {moviesList && (
              <div className="stats-browse-count">
                {moviesList.pagination.total}{' '}
                {moviesList.pagination.total === 1 ? 'movie' : 'movies'}
              </div>
            )}
          </div>

          {/* Filter row */}
          {moviesList?.available_filters && (
            <div className="stats-browse-filter-row">
              <select value={browseGenre} onChange={(e) => setBrowseGenre(e.target.value)}>
                <option value="">All Genres</option>
                {moviesList.available_filters.genres.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>

              <select value={browseLanguage} onChange={(e) => setBrowseLanguage(e.target.value)}>
                <option value="">All Languages</option>
                {moviesList.available_filters.languages.map((l) => (
                  <option key={l} value={l}>
                    {getLanguageName(l)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <MovieGrid movies={moviesList?.movies || []} loading={moviesLoading} />

        {/* Pagination */}
        {moviesList && moviesList.pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={browsePage <= 1}
              onClick={() => setBrowsePage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {moviesList.pagination.page} of {moviesList.pagination.totalPages}
            </span>
            <button
              disabled={browsePage >= moviesList.pagination.totalPages}
              onClick={() => setBrowsePage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </StatsSection>
    </div>
  );
}
